import { useEffect, useMemo, useState } from 'react'
import { ImagePlus, Save } from 'lucide-react'
import { adminApi } from '../api'
import { errorMessage } from '../api/client'

const fields = [
  ['address', 'General address', 'textarea', 'general'],
  ['contact_address', 'Business address', 'textarea', 'general'],
  ['home_hero_eyebrow', 'Home eyebrow text', 'text', 'home'],
  ['home_hero_title', 'Home title', 'text', 'home'],
  ['home_hero_highlight', 'Home red title text', 'text', 'home'],
  ['home_hero_subtitle', 'Home subtitle', 'textarea', 'home'],
  ['home_hero_image_url', 'Home banner image', 'image', 'home'],
  ['page_banner_image_url', 'Inner page banner image', 'image', 'banners'],
]

const defaults = {
  address: '6 Soi Keha Rom Klao 72, Intersection 5,\nRat Phatthana Subdistrict, Saphan Sung District,\nBangkok 10240, Thailand',
  contact_address: '6 Soi Keha Rom Klao 72, Intersection 5,\nRat Phatthana Subdistrict, Saphan Sung District,\nBangkok 10240, Thailand',
  home_hero_eyebrow: 'Find. Buy. Drive.',
  home_hero_title: 'Find Your Perfect Car',
  home_hero_highlight: 'Perfect Car',
  home_hero_subtitle: 'Thousands of verified cars. Best prices. Trusted deals from Thailand to the world.',
  home_hero_image_url: '/images/home/hero-sedan.png',
  page_banner_image_url: 'https://images.unsplash.com/photo-1503736334956-4c8f8e92946d?auto=format&fit=crop&w=1900&q=80',
}

export default function SiteBasics({ notify }) {
  const [rows, setRows] = useState([])
  const [values, setValues] = useState(defaults)
  const [files, setFiles] = useState({})
  const [state, setState] = useState({ loading: true, saving: false, error: '' })
  const byKey = useMemo(() => Object.fromEntries(rows.map(row => [row.key, row])), [rows])

  const load = () => {
    setState(s => ({ ...s, loading: true, error: '' }))
    adminApi.list('site-settings', { per_page: 100 })
      .then(r => {
        const data = Array.isArray(r.data?.data) ? r.data.data : r.data
        setRows(data || [])
        setValues({ ...defaults, ...Object.fromEntries((data || []).map(row => [row.key, row.value || ''])) })
      })
      .catch(err => setState(s => ({ ...s, error: errorMessage(err) })))
      .finally(() => setState(s => ({ ...s, loading: false })))
  }

  useEffect(() => { load() }, [])

  const saveField = async ([key, , type, group]) => {
    const existing = byKey[key]
    const data = new FormData()
    data.append('key', key)
    data.append('value', values[key] ?? '')
    data.append('type', 'string')
    data.append('group', group)
    data.append('is_public', '1')
    if (type === 'image' && files[key]) data.append('image', files[key])
    return existing ? adminApi.updateMultipart('site-settings', existing.id, data) : adminApi.createMultipart('site-settings', data)
  }

  const save = async e => {
    e.preventDefault()
    setState(s => ({ ...s, saving: true, error: '' }))
    try {
      await Promise.all(fields.map(saveField))
      setFiles({})
      notify('Website basics saved.')
      load()
    } catch (err) {
      setState(s => ({ ...s, error: errorMessage(err) }))
    } finally {
      setState(s => ({ ...s, saving: false }))
    }
  }

  const setImage = (key, file) => {
    if (!file?.type?.startsWith('image/')) return
    setFiles(current => ({ ...current, [key]: file }))
    setValues(current => ({ ...current, [key]: URL.createObjectURL(file) }))
  }

  if (state.loading) return <div className="admin-card p-8">Loading website settings...</div>

  return <form onSubmit={save}>
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div><h1 className="text-3xl uppercase">Website Basics</h1><p className="mt-1 text-sm text-[#6b7280]">Edit public banner text, banner images, and contact address.</p></div>
      <button className="admin-btn" disabled={state.saving}><Save size={16}/>{state.saving ? 'Saving...' : 'Save Changes'}</button>
    </div>
    {state.error && <p className="mb-4 rounded bg-red-50 p-4 text-red-700">{state.error}</p>}
    <section className="admin-card admin-section">
      <div className="admin-form-grid">
        {fields.map(([key, label, type]) => type === 'image'
          ? <label key={key} className="admin-field md:col-span-2 xl:col-span-3">{label}
              <span onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setImage(key, e.dataTransfer.files?.[0]) }} className="grid cursor-pointer gap-3 rounded-lg border-2 border-dashed p-4">
                <span className="flex items-center gap-2 text-sm font-bold"><ImagePlus size={17}/>Drop image or select file</span>
                <img src={values[key]} alt="" className="h-48 w-full rounded object-cover"/>
                <input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setImage(key, e.target.files?.[0])}/>
              </span>
            </label>
          : <label key={key} className={`admin-field ${type === 'textarea' ? 'md:col-span-2 xl:col-span-3' : ''}`}>{label}
              {type === 'textarea'
                ? <textarea value={values[key] || ''} onChange={e => setValues({ ...values, [key]: e.target.value })}/>
                : <input value={values[key] || ''} onChange={e => setValues({ ...values, [key]: e.target.value })}/>}
            </label>)}
      </div>
    </section>
  </form>
}
